import React, { useMemo, useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, ArrowRight } from "lucide-react";
import { TRANSFER_POSTS } from "../data/transferBlogs";
import { useLang } from "../i18n/LanguageContext";
import { cityPath } from "../i18n/langPath";

// Homepage city search: type a city name and jump straight to its SEO page.
export default function CitySearch() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return TRANSFER_POSTS.filter((p) => p.name.toLowerCase().includes(term)).slice(0, 8);
  }, [q]);

  const go = (p) => {
    if (!p) return;
    setOpen(false);
    navigate(cityPath(p.citySlug, lang));
  };

  const onKey = (e) => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); go(results[active]); }
  };

  return (
    <section id="city-search" className="py-14 md:py-20 bg-[#faf7f4]">
      <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
        <span className="text-xs uppercase tracking-[0.2em] text-[#F5B400] font-semibold" data-testid="citysearch-eyebrow">{t("ci_eyebrow")}</span>
        <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-black leading-tight">{t("bl_dir_title")}</h2>
        <p className="mt-3 text-neutral-600">{t("bl_dir_sub", { n: TRANSFER_POSTS.length })}</p>

        <div ref={boxRef} className="relative mt-8 text-left">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#F5B400]" />
            <input
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(0); }}
              onFocus={() => q && setOpen(true)}
              onKeyDown={onKey}
              placeholder={t("bl_search_ph")}
              data-testid="citysearch-input"
              className="w-full h-14 rounded-2xl bg-white border border-neutral-200 pl-12 pr-4 text-base text-neutral-900 placeholder:text-neutral-400 shadow-sm focus:outline-none focus:border-[#F5B400] focus:ring-2 focus:ring-[#F5B400]/20"
            />
          </div>
          {open && q.trim() !== "" && (
            <ul className="absolute z-50 mt-2 w-full bg-white border border-neutral-200 rounded-2xl shadow-2xl max-h-80 overflow-auto py-2" data-testid="citysearch-results">
              {results.length ? results.map((p, i) => (
                <li key={p.citySlug}>
                  <button
                    type="button"
                    onMouseEnter={() => setActive(i)}
                    onClick={() => go(p)}
                    data-testid={`citysearch-result-${p.citySlug}`}
                    className={`w-full text-left px-4 py-3 flex items-center justify-between gap-3 ${i === active ? "bg-[#F5B400]/8" : ""} hover:bg-[#F5B400]/8`}
                  >
                    <span className="flex items-center gap-2.5 text-sm text-neutral-800">
                      <MapPin className="w-4 h-4 text-[#F5B400] shrink-0" /> {p.name}
                    </span>
                    <ArrowRight className="w-4 h-4 text-neutral-400" />
                  </button>
                </li>
              )) : (
                <li className="px-4 py-3 text-sm text-neutral-500">{t("bl_no_city")}</li>
              )}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
